import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { DlqItem } from '@control-center/shared';

import { DlqList } from '../../components/admin/dlq/DlqList';
import { RetryConfirmDialog } from '../../components/admin/dlq/RetryConfirmDialog';
import { PillButton } from '../../components/clients/PillButton';
import { Select } from '../../components/clients/form-primitives';
import { PageHelp } from '../../components/common/PageHelp';
import { useAuth } from '../../hooks/use-auth';
import { useDlq, useRetryDlqItem } from '../../hooks/use-admin-notifications';
import { extractErrorMessage } from '../../lib/errors';

type ChannelFilter = 'all' | 'whatsapp' | 'email';

export function AdminNotificationsDlqPage(): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [channelFilter, setChannelFilter] = useState<ChannelFilter>('all');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const [retryOpen, setRetryOpen] = useState(false);
  const [retrying, setRetrying] = useState<DlqItem | null>(null);

  const queryParams = useMemo(
    () => ({
      page,
      pageSize,
      channel: channelFilter === 'all' ? undefined : channelFilter,
    }),
    [page, channelFilter],
  );
  const query = useDlq(queryParams);
  const retryMutation = useRetryDlqItem();

  const items = query.data?.data ?? [];
  const pagination = query.data?.pagination;

  function handleAskRetry(item: DlqItem) {
    setRetrying(item);
    setRetryOpen(true);
  }

  async function handleConfirmRetry() {
    if (!retrying) return;
    try {
      await retryMutation.mutateAsync(retrying.id);
      toast.success('Notificação reenfileirada — saiu da DLQ.');
      setRetryOpen(false);
      setRetrying(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao reprocessar notificação.'));
    }
  }

  function changeChannel(value: ChannelFilter) {
    setChannelFilter(value);
    setPage(1);
  }

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-start gap-2">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-ink">
              DLQ — Notificações falhadas
            </h1>
            <p className="mt-1 text-sm text-muted">
              Comunicações que esgotaram retries automáticos. Reprocessar reseta tentativas e
              re-enfileira.
            </p>
          </div>
          <PageHelp id="admin-dlq" />
        </div>
        {!isAdmin && (
          <span
            className="rounded-[6px] border border-line bg-panel-2 px-3 py-1 text-[12px] text-muted"
            title="Apenas admin pode reprocessar"
          >
            Somente leitura
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <Select
          value={channelFilter}
          onChange={(e) => changeChannel(e.target.value as ChannelFilter)}
        >
          <option value="all">Todos os canais</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="email">Email</option>
        </Select>
        <div className="md:col-span-3 flex items-center justify-end gap-3 text-[12px] text-muted">
          {query.isFetching && <span>Atualizando…</span>}
          {pagination && (
            <span className="tnum">
              {pagination.total} {pagination.total === 1 ? 'item' : 'itens'} · Página{' '}
              {pagination.page} de {pagination.totalPages}
            </span>
          )}
        </div>
      </div>

      {query.isLoading && (
        <div className="rounded-[12px] border border-line bg-panel py-12 text-center text-sm text-muted">
          Carregando DLQ…
        </div>
      )}

      {query.isError && (
        <div className="rounded-[12px] border border-bad/40 bg-bad/10 p-4 text-sm text-ink">
          Erro ao carregar DLQ. Tente recarregar a página.
        </div>
      )}

      {query.data && items.length === 0 && (
        <div className="rounded-[12px] border border-good/40 bg-good/10 py-12 text-center text-sm text-ink">
          ✅ Nada na DLQ — todas as notificações foram entregues.
        </div>
      )}

      {query.data && items.length > 0 && (
        <DlqList
          items={items}
          canRetry={isAdmin}
          onRetry={handleAskRetry}
          retryingId={retryMutation.isPending ? (retrying?.id ?? null) : null}
        />
      )}

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-end gap-2">
          <PillButton
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || query.isFetching}
            icon={<ChevronLeft size={14} strokeWidth={2} />}
          >
            Anterior
          </PillButton>
          <PillButton
            type="button"
            onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
            disabled={page >= pagination.totalPages || query.isFetching}
            icon={<ChevronRight size={14} strokeWidth={2} />}
          >
            Próxima
          </PillButton>
        </div>
      )}

      <RetryConfirmDialog
        open={retryOpen}
        onOpenChange={(open) => {
          setRetryOpen(open);
          if (!open) setRetrying(null);
        }}
        itemSummary={
          retrying
            ? `Cliente: ${retrying.clientName} · Canal: ${retrying.channel} · Template: ${retrying.templateKey ?? '—'}`
            : null
        }
        onConfirm={() => void handleConfirmRetry()}
        isPending={retryMutation.isPending}
      />
    </div>
  );
}
